/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

/**
 * @typedef {Object} ElementTemplateError
 * @property {string} field
 * @property {string} title
 * @property {string} message
 */

import { makeAutoObservable } from 'mobx';

import { generateId } from 'utils/generate-id';

import { sanitizeTemplateName } from './utils';

export const FIELDS_IDS = {
  TEMPLATE_NAME: 'template-name',
  TEMPLATE_DESCRIPTION: 'template-description'
};

export const DEFAULT_FOCUSED_FIELD = FIELDS_IDS.TEMPLATE_NAME;

class SaveElementTemplateStore {
  showSaveAs = false;
  isSaving = false;

  templateId = '';
  templateName = '';
  templateDescription = '';
  focusedField = DEFAULT_FOCUSED_FIELD;

  /**
   * @property {ElementTemplateError[]} errors
   * @type {ElementTemplateError[]}
   */
  errors = [];

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Initializes the store with a template.
   * @param template {Object} The template to initialize the store with.
   */
  init(template) {
    if (template) {
      this.templateId = generateId({ prefix: 'Template_' });
      this.setTemplateName(`${sanitizeTemplateName(template.name)} - Copy`);
      this.setTemplateDescription(template.description);
      this.setShowSaveAs(true);
    }
  }

  reset() {
    this.showSaveAs = false;
    this.isSaving = false;
    this.templateId = '';
    this.templateName = '';
    this.templateDescription = '';
    this.focusedField = FIELDS_IDS.TEMPLATE_NAME;
    this.errors = [];
  }

  /**
   * Sets whether the "Save As" option should be shown in the UI.
   * @param show {boolean} True to show the "Save As" option, false to hide it.
   */
  setShowSaveAs(show) {
    this.showSaveAs = show;
  }

  /**
   * Sets whether a resource is being saved.
   * @param isSaved {boolean} True if a resource is being saved, false otherwise.
   */
  setIsSaving(isSaved) {
    this.isSaving = isSaved;
  }

  /**
   * Sets the currently focussed field ID. This is used to manage focus state in the UI.
   * @param fieldId {string} The ID of the field that is currently focussed.
   */
  setFocusedField(fieldId) {
    this.focusedField = fieldId;
  }

  /**
   * Validates a field based on its ID and value.
   * @param fieldId {string} The ID of the field to validate.
   * @param value {string} The value of the field to validate.
   */
  validateField(fieldId, value) {
    if (fieldId === FIELDS_IDS.TEMPLATE_NAME && !value.length) {
      this.addError({
        field: FIELDS_IDS.TEMPLATE_NAME,
        title: 'Validation Error',
        message: 'Please enter a name for the template.'
      });
    } else {
      this.removeError(fieldId);
    }
  }

  /**
   * Sets the template name and removes any existing error for that field.
   * @param name {string} The name to set for the template.
   */
  setTemplateName(name) {
    this.templateName = name;
    this.removeError(FIELDS_IDS.TEMPLATE_NAME);
  }

  /**
   * Sets the template description.
   * @param description {string} The description to set for the template.
   */
  setTemplateDescription(description) {
    this.templateDescription = description;
  }

  /**
   * Checks if the store has an error for a specific field.
   * @param fieldId {string} The ID of the field to check for errors.
   * @returns {boolean} True if there is an error for the field, false otherwise.
   */
  hasError(fieldId) {
    return this.errors.some((error) => error.field === fieldId);
  }

  get hasErrors() {
    return this.errors.length > 0;
  }

  /**
   * Retrieves the error for a specific field.
   * @param fieldId {string} The ID of the field to retrieve the error for.
   * @return {ElementTemplateError|null} The error object if found, otherwise null.
   */
  getError(fieldId) {
    return this.errors.find((error) => error.field === fieldId) || null;
  }

  /**
   * Adds an error to the store.
   * @param error {ElementTemplateError} The error object to add.
   */
  addError(error) {
    this.removeError(error.field);
    this.errors.push(error);
  }

  /**
   * Removes an error for a specific field from the store.
   * @param fieldId {string} The ID of the field for which to remove the error.
   */
  removeError(fieldId) {
    this.errors = this.errors.filter((error) => error.field !== fieldId);
  }
}

export const saveElementTemplateStore = new SaveElementTemplateStore();
